import { useMemo, useState } from 'react'

import { StatsigClient } from '@statsig/js-client'

import { setStatsigClient } from '../utils/analytics-v2'

/**
 * Maps application environment strings to Statsig tier names.
 * @param env the value of REACT_APP_STAGE
 * @returns the corresponding Statsig tier string, or the original value if unrecognized
 */
function coalesceEnvToStatsigEnv(env?: string): string | undefined {
  switch (env) {
    case 'production':
    case 'prod':
      return 'production'
    case 'staging':
    case 'stage':
    case 'local':
      return 'staging'
    case 'development':
    case 'dev':
      return 'development'
    default:
      return env
  }
}

/**
 * Initializes the Statsig client asynchronously and registers it for use in analytics.
 * Renders the app after initialization completes, with a 5-second fallback timeout.
 * @returns An object containing the Statsig client instance and a boolean indicating whether initialization is still in progress.
 */
export function useAsyncStatsigClient(): { isLoading: boolean; client: StatsigClient } {
  const [isLoading, setIsLoading] = useState(true)

  const client = useMemo(() => {
    const instance = new StatsigClient(
      `${import.meta.env.REACT_APP_STATSIG_CLIENT_SDK_KEY}`,
      {},
      { environment: { tier: coalesceEnvToStatsigEnv(import.meta.env.REACT_APP_STAGE) } }
    )

    const initPromise = instance.initializeAsync()
    initPromise
      .then(() => {
        setStatsigClient(instance)
        setIsLoading(false)
      })
      .catch(err => {
        console.error('Statsig init failed (app will still render):', err)
        setIsLoading(false)
      })
    // If Statsig hangs (e.g. bad key), show app after 5s so local dev isn't stuck
    setTimeout(() => setIsLoading(prev => (prev ? false : prev)), 5000)

    return instance
  }, [])

  return { client, isLoading }
}
